// eslint-disable-next-line 
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogContent,
    Typography,
    IconButton,
    Button,
    LinearProgress
} from '@mui/material';
import { X, UploadCloud, FileText, Trash2 } from 'lucide-react';
import { uploadFiles, updateFilesInProject } from '../../lib/redux/actions';
import toast from '../../lib/utils/toast';
import useLoader from "../../hooks/useLoader";

export default function AddFilesModal({ open, onClose, onAttachFiles, projectType, projectId, fetchProjectDetails, projectFiles }) {


    console.log("projectFiles", projectFiles)
    const [files, setFiles] = useState([]);
    const fileInputRef = useRef(null);
    const progressIntervals = useRef({});
    const { showLoader, hideLoader } = useLoader();

    useEffect(() => {
        if (projectFiles && projectFiles.length > 0) {
            const existingFiles = projectFiles.map((pf) => ({
                id: pf._id || Math.random().toString(36).substr(2, 9),
                file: { name: pf.fileName, size: 0 }, // Minimal mock file object
                progress: 100,
                status: 'completed',
                uploadedUrl: pf.fileUrl,
                fileName: pf.fileName,
                isExisting: true // Mark as existing file from project
            }));
            setFiles(existingFiles);
        } else {
            setFiles([]);
        }
    }, [projectFiles]);


    const validateFiles = (filesToValidate) => {
        let allowedExtensions = [];

        if (projectType === "finance") {
            allowedExtensions = ['.csv', '.xlsx'];
        } else if (projectType === "generic") {
            allowedExtensions = ['.pdf', '.docx', '.csv', '.txt', '.xls', '.xlsx'];
        } else if (projectType === "marketing") {
            // Assuming marketing projects don't allow file uploads or use a different mechanism
            return [];
        }


        const validFiles = filesToValidate.filter(file => {
            const fileName = file.name.toLowerCase();
            const fileExtension = fileName.substring(fileName.lastIndexOf('.'));

            if (projectType === "finance") {
                if (!allowedExtensions.includes(fileExtension)) {
                    toast.error(`File "${file.name}" is not supported. Only CSV and XLSX files are allowed.`);
                    return false;
                }
            } else if (projectType === "generic") {
                if (!allowedExtensions.includes(fileExtension)) {
                    toast.error(`File "${file.name}" is not supported. Supported formats: .pdf, .docx, .csv, .txt, .xls, .xlsx`);
                    return false;
                }
            }
            return true;
        });

        return validFiles;
    };


    const handleFileChange = (event) => {
        const selectedFiles = Array.from(event.target.files);
        const validFiles = validateFiles(selectedFiles);

        if (validFiles.length === 0) {
            if (selectedFiles.length > 0) event.target.value = "";
            return;
        }

        const newFiles = validFiles.map(file => ({
            file,
            id: Math.random().toString(36).substr(2, 9),
            progress: 0,
            status: 'pending', // pending, uploading, completed, error
            uploadedUrl: null
        }));
        setFiles(prev => [...prev, ...newFiles]);
        uploadFilesToS3(newFiles);
    };

    const uploadFilesToS3 = async (newFiles) => {
        try {
            // Start progress animation from 0 to 80% over 5 seconds for ALL files
            const progressDuration = 5000; // 5 seconds
            const progressTarget = 80; // Stop at 80%
            const intervalTime = 100; // Update every 100ms
            const progressIncrement = (progressTarget / progressDuration) * intervalTime;

            let currentProgress = 0;
            const progressIntervalId = setInterval(() => {
                currentProgress += progressIncrement;
                if (currentProgress >= progressTarget) {
                    clearInterval(progressIntervalId);
                    currentProgress = progressTarget;
                }
                // Update progress for all files being uploaded
                setFiles(prev => prev.map(f => {
                    const isBeingUploaded = newFiles.some(nf => nf.id === f.id);
                    return isBeingUploaded ? { ...f, progress: currentProgress } : f;
                }));
            }, intervalTime);

            // Store interval ID for cleanup
            progressIntervals.current['batch'] = progressIntervalId;

            // Upload ALL files in a single batch API call
            const formData = new FormData();
            newFiles.forEach((fileObj) => {
                formData.append("files", fileObj.file);
            });

            const result = await uploadFiles(formData);

            // Clear interval and set to 100% on success
            if (progressIntervals.current['batch']) {
                clearInterval(progressIntervals.current['batch']);
                delete progressIntervals.current['batch'];
            }

            if (result.statusCode === 200 && result.data && result.data.length > 0) {
                // Map each uploaded URL to the corresponding file
                setFiles(prev => prev.map(f => {
                    const fileIndex = newFiles.findIndex(nf => nf.id === f.id);
                    if (fileIndex !== -1 && result.data[fileIndex]) {
                        return {
                            ...f,
                            progress: 100,
                            status: 'completed',
                            uploadedUrl: result.data[fileIndex]
                        };
                    }
                    return f;
                }));
            } else {
                throw new Error('Upload failed');
            }
        } catch (error) {
            // Clear interval on error
            if (progressIntervals.current['batch']) {
                clearInterval(progressIntervals.current['batch']);
                delete progressIntervals.current['batch'];
            }

            // Mark all files as error
            setFiles(prev => prev.map(f => {
                const isFailedFile = newFiles.some(nf => nf.id === f.id);
                return isFailedFile ? { ...f, progress: 0, status: 'error' } : f;
            }));

            toast.error(`Failed to upload files`);
            console.error('File upload error:', error);
        }
    };

    const handleRemoveFile = (id) => {
        if (files.length <= 1) {
            toast.error("At least one file is required.");
            return;
        }
        setFiles(prev => prev.filter(f => f.id !== id));
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const droppedFiles = Array.from(e.dataTransfer.files);
        const validFiles = validateFiles(droppedFiles);

        if (validFiles.length === 0) return;

        const newFiles = validFiles.map(file => ({
            file,
            id: Math.random().toString(36).substr(2, 9),
            progress: 0,
            status: 'pending',
            uploadedUrl: null
        }));
        setFiles(prev => [...prev, ...newFiles]);
        uploadFilesToS3(droppedFiles);
    };

    const handleAttach = async () => {
        const completedFiles = files
            .filter(f => f.status === 'completed')
            .map(f => ({
                fileUrl: f.uploadedUrl,
                fileName: f.fileName || f.file.name
            }));

        if (completedFiles.length === 0) return;

        showLoader();
        try {
            await updateFilesInProject({
                projectId,
                files: completedFiles
            });

            onAttachFiles(completedFiles);
            setFiles([]);
            fetchProjectDetails();
            onClose();
        } catch (error) {
            console.error("Failed to update project files:", error);
            toast.error("Failed to attach files");
        } finally {
            hideLoader();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                style: { borderRadius: 16, padding: '24px', maxWidth: '700px' }
            }}
        >
            <div className="flex justify-between items-center mb-2">
                <Typography variant="h5" style={{ fontWeight: 400 }}>
                    Project files
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <X className="w-5 h-5 text-gray-500" />
                </IconButton>
            </div>

            <Typography variant="body2" color="textSecondary" className="mb-6">
                Add documents, code files, and more. New can access their contents when you chat inside the project.
            </Typography>

            <DialogContent className="p-0">
                {/* Drag & Drop Area */}
                <div
                    className="border-2 border-dashed border-gray-200 rounded-xl p-8 flex flex-col items-center justify-center mb-6 cursor-pointer hover:bg-gray-50 transition-colors"
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <input
                        type="file"
                        multiple
                        ref={fileInputRef}
                        className="hidden"
                        onChange={handleFileChange}
                        accept={
                            projectType === "finance"
                                ? ".csv, .xlsx"
                                : projectType === "generic"
                                    ? ".pdf, .docx, .csv, .txt, .xls, .xlsx"
                                    : ""
                        }
                    />
                    <UploadCloud className="w-10 h-10 text-green-700 mb-3" />
                    <Typography variant="body1" className="text-gray-900 font-medium">
                        <span className="text-green-700 underline">Choose a file</span> or drag and drop it here
                    </Typography>
                    {projectType === "finance" && (
                        <Typography variant="caption" className="text-gray-500 mt-2">
                            Supported formats: CSV, XLSX
                        </Typography>
                    )}
                    {projectType === "generic" && (
                        <Typography variant="caption" className="text-gray-500 mt-2">
                            Supported formats: PDF, DOCX, CSV, TXT, XLS, XLSX
                        </Typography>
                    )}
                </div>

                {/* File List */}
                <div className="flex flex-col gap-3 mb-6">
                    {files.map((fileObj) => (
                        <div key={fileObj.id} className="border border-gray-100 rounded-lg p-4 flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1">
                                <div className="bg-red-50 p-2 rounded">
                                    {/* Placeholder for PDF/Doc icon logic if needed */}
                                    <FileText className="w-6 h-6 text-red-500" />
                                </div>
                                <div className="flex-1">
                                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-1">
                                        <Typography variant="body2" className="font-medium text-gray-900 truncate algin-left sm:max-w-[200px] max-w-[100px] ">
                                            {fileObj.file.name}
                                        </Typography>
                                        {fileObj.status === 'completed' ? (
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs text-green-700 font-medium flex items-center gap-1">
                                                    Completed
                                                </span>
                                                {/* Only show delete button for newly uploaded files, not existing project files */}
                                                {!fileObj.isExisting && (
                                                    <IconButton size="small" onClick={() => handleRemoveFile(fileObj.id)}>
                                                        <Trash2 className="w-4 h-4 text-red-400" />
                                                    </IconButton>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs text-gray-500">
                                                    Uploading...
                                                </span>
                                                <IconButton size="small" onClick={() => handleRemoveFile(fileObj.id)}>
                                                    <X className="w-4 h-4 text-gray-400" />
                                                </IconButton>
                                            </div>
                                        )}
                                    </div>
                                    <LinearProgress
                                        variant="determinate"
                                        value={fileObj.progress}
                                        sx={{
                                            height: 6,
                                            borderRadius: 3,
                                            backgroundColor: '#F3F4F6',
                                            '& .MuiLinearProgress-bar': {
                                                backgroundColor: '#15803d'
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="flex justify-center">
                    <Button
                        variant="contained"
                        onClick={handleAttach}
                        disabled={files.length === 0 || files.some(f => f.status !== 'completed')}
                        style={{
                            backgroundColor: "#1B5C29",
                            color: "white",
                            textTransform: "none",
                            fontWeight: 500,
                            borderRadius: "8px",
                            padding: "8px 32px",
                            fontSize: "15px"
                        }}
                    >
                        Update files
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

AddFilesModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onAttachFiles: PropTypes.func.isRequired,
    projectFiles: PropTypes.array
};
